import PurchaseHistory from "../components/PurchaseHistory";
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import WebApp from "@twa-dev/sdk";
import { safeOpenTelegramLink } from "../utils/linkHelpers";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL } from "../config";
import { FaGem, FaUserCircle, FaCopy, FaShareAlt, FaCheckCircle, FaClock, FaTrophy, FaSun, FaMoon, FaFilm, FaChevronRight } from "react-icons/fa";
import { ImSpinner2 } from "react-icons/im";
import { motion, AnimatePresence } from "framer-motion";
import { useTitle } from "../hooks/useTitle";
import ReactGA from "react-ga4";
import AdSense from "react-adsense";
import { useUser } from "../context/UserContext";
import LiquidGlass from "../components/LiquidGlass";
import haptic from "../utils/haptic";
import { useTheme } from "../context/ThemeContext";
import { TonConnectButton } from "@tonconnect/ui-react";
import { useLocation, useNavigate } from "react-router-dom";

const MILESTONES = [
  { count: 10,   bonus: 100   },
  { count: 20,   bonus: 200   },
  { count: 50,   bonus: 500   },
  { count: 100,  bonus: 1000  },
  { count: 200,  bonus: 2000  },
  { count: 500,  bonus: 5000  },
  { count: 1000, bonus: 10000 },
];

const ProfilePage = () => {
  useTitle("Profile");
  const { userData, setUserData, refreshUser, loading: contextLoading, APIKey } = useUser();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const referralRef = useRef(null);
  const [referralURL, setReferralURL] = useState("");
  const [referredUsers, setReferredUsers] = useState([]);
  const [purchasedMovies, setPurchasedMovies] = useState([]);
  const [moviesLoading, setMoviesLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchReferredUsers = async (userId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/user/${userId}/referred`,
        {
          params: { initData: WebApp.initData },
          headers: { "x-api-key": APIKey },
        }
      );
      setReferredUsers(response.data);
    } catch (error) {
      console.error("Error fetching referred users:", error);
      toast.error("Failed to load referred users. Please try again.");
    }
  };

  const fetchPurchasedMovies = async (userId) => {
    try {
      setMoviesLoading(true);
      const response = await axios.get(
        `${API_URL}/api/user/${userId}/purchased-movies`,
        {
          params: { initData: WebApp.initData },
          headers: { "x-api-key": APIKey },
        }
      );
      setPurchasedMovies(response.data);
    } catch (error) {
      console.error("Error fetching purchased movies:", error);
    } finally {
      setMoviesLoading(false);
    }
  };

  const copyToClipboard = () => {
    ReactGA.event({ category: "Referral", action: "Referral URL copied", label: "Referral Page" });
    navigator.clipboard.writeText(referralURL).then(() => {
      setCopied(true);
      toast.success("Referral URL copied!", { position: "bottom-center", autoClose: 2000, closeOnClick: true });
      setTimeout(() => setCopied(false), 2000);
    });
  };

  useEffect(() => {
    if (!contextLoading && userData.user) {
      setReferralURL(`https://t.me/biscopemyanmar_bot/store?startapp=${userData.user.referralCode}`);
      fetchReferredUsers(userData.userId);
      fetchPurchasedMovies(userData.userId);
      setLoading(false);
    }
  }, [contextLoading, userData.user]);

  // Scroll to referral section if navigated with scrollTo state
  useEffect(() => {
    if (location.state?.scrollTo === "referral" && referralRef.current) {
      const timer = setTimeout(() => {
        referralRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [location.state, referralRef.current]);

  const totalReferrals = referredUsers.length;
  const rewardedReferrals = referredUsers.filter(u => u.referralTaskCompleted).length;

  // Next milestone the user hasn't reached yet
  const nextMilestone = MILESTONES.find(m => rewardedReferrals < m.count);
  const prevMilestone = MILESTONES.slice().reverse().find(m => rewardedReferrals >= m.count);

  const progressFrom = prevMilestone?.count ?? 0;
  const progressTo   = nextMilestone?.count ?? MILESTONES[MILESTONES.length - 1].count;
  const progressPct  = nextMilestone
    ? Math.round(((rewardedReferrals - progressFrom) / (progressTo - progressFrom)) * 100)
    : 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="bg-slate-50 dark:bg-gradient-to-br dark:from-gray-900 dark:to-black text-slate-900 dark:text-white min-h-screen pb-24 transition-colors duration-300"
    >
      <div className="container mx-auto p-4 max-w-lg space-y-5 pt-20">

        {/* Hero banner */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-2xl overflow-hidden relative"
        >
          <div className="bg-gradient-to-r from-[#0C8BC4] via-sky-500 to-cyan-600 dark:from-sky-800 dark:via-[#0C8BC4] dark:to-cyan-900 p-5">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl pointer-events-none" />
            <h2 className="text-white font-extrabold text-2xl">Invite Friends,<br />Earn Gems!</h2>
            <p className="text-white/80 text-sm mt-1">You earn <span className="font-bold text-amber-300">100 <FaGem className="inline" /></span> for each friend who joins and completes their first task.</p>
            <p className="text-white/60 text-xs mt-1">Your friend also gets a <span className="text-white/80 font-semibold">+100 <FaGem className="inline" /></span> welcome bonus!</p>
          </div>
        </motion.div>

        {/* Display Settings */}
        <div className="flex items-center justify-between mb-2 bg-white dark:bg-slate-800 p-5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700/50">
          <div>
            <h2 className="font-bold text-slate-800 dark:text-white flex items-center gap-2">
              {theme === "dark" ? <FaMoon size={14} className="text-indigo-400" /> : <FaSun size={14} className="text-amber-500" />}
              Display
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Toggle light / dark mode</p>
          </div>
          <button
            onClick={toggleTheme}
            className="p-2.5 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 transition-colors shadow-sm text-slate-800 dark:text-yellow-300 border border-slate-200 dark:border-slate-600"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <FaSun size={18} /> : <FaMoon size={18} />}
          </button>
        </div>

        {/* Web3 Settings */}
        <div className="flex items-center justify-between mb-2 bg-white dark:bg-slate-800 p-5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700/50">
          <div>
            <h2 className="font-bold text-slate-800 dark:text-white flex items-center gap-2">
              💎 Web3
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Connect TON wallet for crypto features</p>
          </div>
          <TonConnectButton style={{ transform: "scale(0.9)", transformOrigin: "right center" }} />
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 gap-3">
          <LiquidGlass className="rounded-2xl p-4 border border-slate-200 dark:border-white/10 text-center">
            <p className="text-3xl font-extrabold text-purple-600 dark:text-purple-400">{totalReferrals}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Friends Invited</p>
          </LiquidGlass>
          <LiquidGlass className="rounded-2xl p-4 border border-slate-200 dark:border-white/10 text-center">
            <p className="text-3xl font-extrabold text-amber-600 dark:text-amber-400">
              {referredUsers.filter(u => u.referralTaskCompleted).length}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Task Completed</p>
          </LiquidGlass>
        </div>

        {/* Milestone progress */}
        <LiquidGlass className="p-5 rounded-2xl border border-slate-200 dark:border-white/10">
          <div className="flex items-center gap-2 mb-3">
            <FaTrophy className="text-amber-500" />
            <h3 className="font-bold text-slate-800 dark:text-white">Milestone Progress</h3>
            <span className="ml-auto text-sm font-semibold text-slate-600 dark:text-slate-300">
              {rewardedReferrals} / {progressTo}
            </span>
          </div>
          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2.5 overflow-hidden mb-3">
            <motion.div
              className="bg-gradient-to-r from-amber-500 to-amber-300 h-2.5 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 1, delay: 0.4 }}
            />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 text-center mb-4">
            {nextMilestone
              ? `${progressTo - rewardedReferrals} more friends to earn +${nextMilestone.bonus.toLocaleString()} gems bonus!`
              : "🎉 All milestones reached — you're a legend!"}
          </p>

          {/* Milestone list */}
          <div className="space-y-2">
            {MILESTONES.map(m => {
              const reached = rewardedReferrals >= m.count;
              const isNext  = m === nextMilestone;
              return (
                <div
                  key={m.count}
                  className={`flex items-center justify-between rounded-xl px-3 py-2 text-sm transition-colors ${
                    reached
                      ? "bg-[#0C8BC4]/10 dark:bg-[#0C8BC4]/20 text-[#0C8BC4]"
                      : isNext
                      ? "bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-500/40 text-slate-700 dark:text-slate-200"
                      : "bg-slate-100 dark:bg-slate-800/60 text-slate-400 dark:text-slate-500"
                  }`}
                >
                  <span className="font-semibold flex items-center gap-1.5">
                    {reached
                      ? <FaCheckCircle className="text-[#0C8BC4]" size={12} />
                      : isNext
                      ? <FaClock className="text-amber-500" size={12} />
                      : <span className="w-3 h-3 rounded-full bg-slate-300 dark:bg-slate-600 inline-block" />
                    }
                    {m.count} friends
                  </span>
                  <span className="font-bold flex items-center gap-1">+{m.bonus.toLocaleString()} <FaGem /></span>
                </div>
              );
            })}
          </div>
        </LiquidGlass>

        {/* Referral link */}
        <div ref={referralRef}>
        <LiquidGlass
          className="p-5 rounded-2xl border border-slate-200 dark:border-white/10"
          glassColor={{
            from: "from-white/25", via: "via-white/15", to: "to-white/10",
            overlayFrom: "from-blue-400/20", overlayVia: "via-purple-400/15", overlayTo: "to-pink-400/10",
            glowFrom: "from-white/10", glowTo: "to-transparent",
          }}
        >
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2">Your referral link</p>
          <div className="flex items-center justify-center border border-slate-200 dark:border-white/10 p-3 rounded-xl bg-slate-100 dark:bg-gray-800 shadow-sm">
            {loading
              ? <ImSpinner2 className="text-teal-600 dark:text-teal-300 animate-spin text-xl" />
              : <p className="break-all text-teal-700 dark:text-teal-300 text-sm font-medium">{referralURL}</p>
            }
          </div>
          <div className="flex gap-3 mt-4">
            <motion.button
              whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
              className="flex-1 bg-[#9333EA] hover:bg-[#7e22ce] text-white font-semibold py-2.5 px-4 rounded-xl transition shadow-md flex items-center justify-center gap-2"
              onClick={() => {
                haptic.tap();
                safeOpenTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(referralURL)}`);
              }}
            >
              <FaShareAlt size={14} /> Invite Friend
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
              onClick={() => { haptic.tap(); copyToClipboard(); }}
              className="flex-1 bg-slate-200 dark:bg-gray-700 hover:bg-slate-300 dark:hover:bg-gray-600 text-slate-700 dark:text-teal-300 font-semibold py-2.5 px-4 rounded-xl transition shadow-sm flex items-center justify-center gap-2 border border-slate-300 dark:border-transparent"
            >
              <FaCopy size={14} /> {copied ? "Copied!" : "Copy Link"}
            </motion.button>
          </div>
        </LiquidGlass>
        </div>

        <AdSense.Google
          client="ca-pub-4252052358641832" slot="4385377268"
          style={{ display: "block" }} format="auto" responsive="true" layoutKey="-gw-1+2a-9x+5c"
        />
        {/* Purchased Movies */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-bold text-slate-700 dark:text-slate-200 flex items-center gap-2">
              <FaFilm className="text-purple-500" /> Purchased Movies
            </h3>
            {purchasedMovies.length > 0 && (
              <button
                onClick={() => { haptic.tap(); navigate("/purchased-movies"); }}
                className="text-sm font-semibold text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 flex items-center gap-1 transition-colors"
              >
                See All <FaChevronRight size={12} />
              </button>
            )}
          </div>
          {moviesLoading ? (
            <div className="flex justify-center py-6">
              <ImSpinner2 className="text-purple-500 animate-spin text-2xl" />
            </div>
          ) : purchasedMovies.length > 0 ? (
            <div className="flex overflow-x-auto gap-4 pb-2 snap-x hide-scrollbar">
              {purchasedMovies.slice(0, 4).map((movie) => (
                <div
                  key={movie._id}
                  className="min-w-[200px] w-[200px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 rounded-xl shadow-sm flex flex-col snap-start shrink-0"
                >
                  <h4 className="font-bold text-slate-800 dark:text-slate-100 text-sm mb-1 truncate">
                    {movie.name || movie.channelName}
                  </h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-3 line-clamp-2 flex-grow min-h-[32px]">
                    {movie.description || "No description available."}
                  </p>
                  <button
                    onClick={() => { haptic.tap(); safeOpenTelegramLink(movie.movie_url || movie.url); }}
                    className="mt-auto w-full py-2 bg-[#9333EA] hover:bg-[#7e22ce] text-white rounded-lg text-xs font-bold transition-all shadow-sm flex items-center justify-center gap-2"
                  >
                    <FaFilm /> Watch Now
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 rounded-xl text-center">
              <p className="text-slate-500 dark:text-slate-400 text-sm">No purchased movies yet.</p>
            </div>
          )}
        </div>

        {/* Referred friends list */}
        <div>
          <h3 className="text-base font-bold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-2">
            <FaUserCircle className="text-purple-500" /> Referred Friends
            <span className="ml-auto bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-xs font-semibold px-2 py-0.5 rounded-full">{totalReferrals}</span>
          </h3>
          <AnimatePresence>
            {referredUsers.length > 0 ? (
              <motion.ul
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }} className="space-y-2"
              >
                {referredUsers.map((user, index) => (
                  <motion.li
                    key={user.userId}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.06 }}
                    className="flex items-center gap-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 rounded-xl shadow-sm"
                  >
                    <FaUserCircle className="text-slate-400 dark:text-slate-500 text-2xl shrink-0" />
                    <div className="flex-grow min-w-0">
                      <p className="font-medium text-slate-800 dark:text-slate-200 truncate">
                        {user.firstName} {user.lastName}
                      </p>
                      {user.username && (
                        <p className="text-slate-400 dark:text-slate-500 text-xs">@{user.username}</p>
                      )}
                    </div>
                    {/* Task completion badge */}
                    {user.referralTaskCompleted ? (
                      <span className="shrink-0 flex items-center gap-1 bg-[#0C8BC4]/10 dark:bg-[#0C8BC4]/20 text-[#0C8BC4] text-xs font-semibold px-2 py-0.5 rounded-full">
                        <FaCheckCircle size={10} /> Rewarded
                      </span>
                    ) : (
                      <span className="shrink-0 flex items-center gap-1 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-xs font-semibold px-2 py-0.5 rounded-full">
                        <FaClock size={10} /> Pending
                      </span>
                    )}
                  </motion.li>
                ))}
              </motion.ul>
            ) : (
              <motion.p
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="text-slate-400 dark:text-slate-500 text-center py-8"
              >
                No friends referred yet.<br />
                <span className="text-sm">Share your link to start earning!</span>
              </motion.p>
            )}
          </AnimatePresence>
        </div>

      </div>
      <PurchaseHistory userId={userData.userId || userData.user?.userId} APIKey={APIKey} onRefresh={refreshUser} />
    </motion.div>
  );
};

export default ProfilePage;
